import { tr } from "../i18n";
import { useEffect, useState } from "react";
import axios from "axios";
import { Search, ShieldCheck, ShieldX, HelpCircle } from "lucide-react";
import { PageHero } from "./Shared";
import { Reveal, Kicker } from "../components/motion";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export default function Verify() {
  const [certId, setCertId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    axios.get(`${API}/certificates/samples`).then(r => setSamples(r.data)).catch(() => {});
  }, []);
  const verify = async e => {
    e?.preventDefault();
    if (!certId.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.get(`${API}/certificates/verify/${encodeURIComponent(certId.trim())}`);
      setResult(r.data);
    } catch {
      setResult({
        status: "not_found",
        certificate_id: certId.trim().toUpperCase()
      });
    } finally {
      setLoading(false);
    }
  };
  const badge = status => {
    if (status === "valid") return <span className="inline-flex items-center gap-2 border border-emerald-600/30 bg-emerald-50 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald-700" data-testid="cert-verify-status-badge">
          <ShieldCheck size={15} />{" "}{tr("Valid")}{" "}</span>;
    if (status === "expired") return <span className="inline-flex items-center gap-2 border border-red-600/30 bg-red-50 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-red-700" data-testid="cert-verify-status-badge">
          <ShieldX size={15} />{" "}{tr("Expired")}{" "}</span>;
    return <span className="inline-flex items-center gap-2 border border-amber-600/30 bg-amber-50 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-700" data-testid="cert-verify-status-badge">
        <HelpCircle size={15} />{" "}{tr("Not found")}{" "}</span>;
  };
  return <div data-testid="verify-page">
      <PageHero kicker={tr("Certificate Verification")} title={tr("Verify an ESAIA Certificate.")} copy={tr("Enter a certificate ID or scan the QR code on the certificate. Only publicly verifiable certification information is displayed; confidential audit details are never exposed.")} />

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <form onSubmit={verify} className="border border-esaia-line bg-white p-8 sm:p-10">
            <label htmlFor="cert-id" className="mb-1.5 block text-xs font-medium text-esaia-ink">{tr("Certificate ID")}{" "}</label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input id="cert-id" value={certId} onChange={e => setCertId(e.target.value)} placeholder={tr("e.g. ESAIA-2026-0001")} data-testid="cert-verify-input" className="flex-1 border border-esaia-line bg-white px-4 py-3 font-mono text-sm uppercase tracking-wider text-esaia-ink placeholder:normal-case placeholder:tracking-normal placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
              <button type="submit" disabled={loading} data-testid="cert-verify-submit-button" className="inline-flex items-center justify-center gap-2 bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark disabled:opacity-60">
                <Search size={16} />
                {loading ? tr("Verifying...") : tr("Verify")}
              </button>
            </div>
            {samples.length > 0 && <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-xs text-esaia-subtle">{tr("Try a sample:")}</span>
                {samples.map(s => <button key={s.certificate_id} type="button" onClick={() => setCertId(s.certificate_id)} data-testid={`sample-cert-${s.certificate_id}`} className="border border-esaia-line px-3 py-1 font-mono text-[11px] text-esaia-muted transition-colors hover:border-esaia-blue hover:text-esaia-blue">
                    {s.certificate_id}
                  </button>)}
              </div>}
          </form>
        </Reveal>

        {result && <Reveal y={16} className="mt-6">
            <div className="border border-esaia-line bg-white p-8 sm:p-10" data-testid="cert-verify-result">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-esaia-line pb-5">
                <div>
                  <Kicker>{tr("Verification result")}</Kicker>
                  <p className="mt-2 font-mono text-sm font-semibold text-esaia-ink">{result.certificate_id}</p>
                </div>
                {badge(result.status)}
              </div>
              {result.status !== "not_found" ? <dl className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {[["Organization", result.organization], ["Certification type", result.type], ["Standard", result.standard], ["Issue date", result.issue_date], ["Expiry date", result.expiry_date], ["Entities in scope", result.entities]].map(([k, v]) => <div key={k}>
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr(k)}</dt>
                      <dd className="mt-1.5 text-sm font-medium text-esaia-ink">{v}</dd>
                    </div>)}
                  <div className="sm:col-span-2">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("Assessment scope")}</dt>
                    <dd className="mt-1.5 text-sm leading-relaxed text-esaia-muted">{result.scope}</dd>
                  </div>
                </dl> : <p className="mt-6 text-sm leading-relaxed text-esaia-muted">{tr("No certificate was found with this ID. Check the identifier on the certificate and try again, or contact info@esaia.org.")}{" "}</p>}
              <p className="mt-6 border-t border-esaia-line pt-4 text-[11px] leading-relaxed text-esaia-subtle">{tr("This portal displays publicly verifiable certification information only. Confidential audit findings are never disclosed.")}{" "}</p>
            </div>
          </Reveal>}
      </section>
    </div>;
}
